import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  type OAuthClientResponse,
  platformDeleteOauthClientMutation,
  platformListOauthClientsQueryKey,
  platformUpdateOauthClientMutation,
} from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/** Enable/disable (immediate) and delete (confirm first) for the OAuth clients table. */
export function useOAuthClientActions() {
  const { t } = useTranslation("adminPlatform");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<OAuthClientResponse | null>(null);
  const update = useMutation(platformUpdateOauthClientMutation());
  const remove = useMutation(platformDeleteOauthClientMutation());
  const refresh = () => queryClient.invalidateQueries({ queryKey: platformListOauthClientsQueryKey() });
  const failed = (error: unknown) => toast.error(errorMessage(error, t("oauthClients.confirm.failed")));

  const setEnabled = (client: OAuthClientResponse, isEnabled: boolean) =>
    update.mutate(
      { path: { client_uuid: client.uuid }, body: { isEnabled } },
      {
        onSuccess: () => {
          toast.success(
            t(isEnabled ? "oauthClients.confirm.enabled" : "oauthClients.confirm.disabled", {
              name: client.clientName,
            }),
          );
          void refresh();
        },
        onError: failed,
      },
    );

  const confirmDelete = () => {
    if (!deleting) return;
    const name = deleting.clientName;
    remove.mutate(
      { path: { client_uuid: deleting.uuid } },
      {
        onSuccess: () => {
          toast.success(t("oauthClients.confirm.deleted", { name }));
          setDeleting(null);
          void refresh();
        },
        onError: failed,
      },
    );
  };

  const dialog = (
    <ConfirmDialog
      open={deleting !== null}
      onClose={() => setDeleting(null)}
      title={deleting ? t("oauthClients.confirm.deleteTitle", { name: deleting.clientName }) : undefined}
      body={t("oauthClients.confirm.deleteBody")}
      confirmLabel={t("oauthClients.confirm.delete")}
      loading={remove.isPending}
      onConfirm={confirmDelete}
    />
  );

  return { setEnabled, requestDelete: setDeleting, dialog };
}
